<template>
  <TableComp
    :headers="headers"
    :name="$t('global.certificates')"
    :status="certificates.status"
    :pagination="certificates.pagination"
    :sort="certificates.sort"
    :number-of-items="certificates.list.length"
    v-on:update:list="updateList"
  >
    <template #desktop>
      <TableRow v-for="cert in certificates.list" :key="cert.id">
        <td class="align-middle">{{ cert.commonName }}</td>
        <td class="align-middle text-center">
          {{ cert.serviceId }}
        </td>
        <td class="align-middle text-center">
          <p v-for="(name, index) in cert.altNames" :key="index">
            {{ name }}
          </p>
        </td>
        <td class="align-middle text-center">
          <span v-if="cert.packageIdentity">{{ cert.packageIdentity }}</span>
          <span v-else>{{ $t("global.imported") }}</span>
        </td>
        <td class="align-middle text-center">
          {{ cert.formattedValidTo }}
        </td>
        <td class="align-middle text-center">
          <Pill
            :content="$t(`global.${getStatus(cert)}`)"
            :variant="getStatusVariant(getStatus(cert))"
          />
        </td>
        <td class="text-end actions">
          <div class="d-flex justify-content-end mr-4">
            <ThreeDotsMenu :items="menuItems(cert)" />
          </div>
        </td>
      </TableRow>
    </template>
    <template #mobile>
      <TableMobileRow
        v-for="cert in certificates.list"
        v-bind:key="cert.id"
        :menu-items="menuItems(cert)"
      >
        <template #header>{{ cert.commonName }}</template>
        <template #content>
          <div class="row">
            <div class="col">{{ $t("global.service-id") }}:</div>
            <div class="col">{{ cert.serviceId }}</div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.additional-domains") }}:</div>
            <div class="col">
              <p v-for="(name, index) in cert.altNames" :key="index">
                {{ name }}
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.type") }}:</div>
            <div class="col">
              <span v-if="cert.packageIdentity">{{ cert.packageIdentity }}</span>
              <span v-else>{{ $t("global.imported") }}</span>
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.valid-until") }}:</div>
            <div class="col">{{ cert.formattedValidTo }}</div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.status") }}:</div>
            <div class="col">
              <Pill
                :content="$t(`global.${getStatus(cert)}`)"
                :variant="getStatusVariant(getStatus(cert))"
              />
            </div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import { canDelete } from "@/utils/useAuth";
import ConfirmationModal from "@/components/display/ConfirmationModal";
import { getStatusVariant } from "@/utils/statusVariant";
import NavLink from "@/classes/NavLink";
import Pill from "@/components/display/Pill";
import ShowCertModal from "@/components/smart/modals/ShowCert";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import TableRow from "@/components/display/table/TableRow";
import ThreeDotsMenu from "@/components/display/ThreeDotsMenu";

export default {
  name: "CertificatesTable",
  props: {
    certificates: Object,
  },
  components: {
    Pill,
    TableComp,
    TableMobileRow,
    TableRow,
    ThreeDotsMenu,
  },
  setup(props, { root }) {
    const headers = [
      new TableHeader(root.$t("global.domain")),
      new TableHeader(root.$t("global.service-id"), "justify-content-center"),
      new TableHeader(root.$t("global.additional-domains"), "justify-content-center"),
      new TableHeader(root.$t("global.type"), "justify-content-center"),
      new TableHeader(root.$t("global.valid-until"), "justify-content-center"),
      new TableHeader(root.$t("global.status"), "justify-content-center"),
      new TableHeader(),
    ];

    const showCert = (cert) => {
      store.dispatch("modals/open", {
        template: ShowCertModal,
        data: { cert },
      });
    };

    const confirmDeleteCert = (cert) => {
      store.dispatch("toasts/addToast", {
        variant: "info",
        message: root.$t("toasts.ssl-deleting-message"),
      });
      return store.dispatch("certificates/delete", cert.id);
    };

    const deleteCert = (cert) => {
      store.dispatch("modals/open", {
        template: ConfirmationModal,
        data: {
          confirm: confirmDeleteCert.bind(null, cert),
          errorMessage: root.$t("global.error-deleting-ssl", { name: cert.commonName }),
          message: root.$t("global.confirm-ssl-delete", { name: cert.commonName }),
          title: root.$t("global.delete-ssl"),
          variant: "danger",
        },
      });
    };

    const menuItems = (cert) => {
      const items = [
        new NavLink(root.$t("global.view"), null, () => showCert(cert), false, "default"),
      ];

      if (cert.id > 0) {
        items.push(
          new NavLink(root.$t("global.order"), null, {
            name: "certificate-add-with-cert",
            params: { certId: cert.id },
          })
        );
      }

      if (canDelete("services")) {
        items.push(new NavLink(root.$t("global.delete"), null, () => deleteCert(cert), false));
      }
      return items.filter((i) => i !== null);
    };

    const getStatus = (cert) => {
      let status = "expired";

      if (cert.id === 0) {
        status = cert.isExpired ? "expired" : "ready-to-use";
      } else {
        status = cert.isExpired && !cert.serviceId ? "expired" : "ready-to-use";

        if (!cert.crt) {
          status = "in-progress";
        }
      }
      return status;
    };

    const updateList = (event) => {
      const { pagination, sort } = event;
      store.dispatch("certificates/getList", {
        pageIndex: pagination?.index,
        pageSize: pagination?.size,
        sortProperty: sort?.property,
        sortDirection: sort?.direction,
      });
    };

    return {
      getStatus,
      getStatusVariant,
      headers,
      menuItems,
      updateList,
    };
  },
};
</script>
